/**
 * Support conversations for the signed-in customer.
 *
 * BFF: `GET /api/portal/support/messages` (own threads), `GET …/:id` (one
 * thread, 404 when it is not the caller's), `POST …` (new thread) and
 * `POST …/:id/replies`. Provide Cars staff answer from PSM; their messages
 * arrive with author "support" and no staff name.
 */
import { PortalApiError, PortalFeatureDisabledError, apiGet, apiPost } from './api';
import { parsePortalTime } from './portalTime';

export type SupportAuthor = 'customer' | 'support';

export interface SupportThread {
  id: number;
  subject: string;
  body: string;
  status: string;
  created_at: string;
  updated_at: string | null;
  last_activity_at: string;
  last_author: SupportAuthor;
  reply_count: number;
}

export interface SupportEntry {
  id: string;
  author: SupportAuthor;
  body: string;
  created_at: string;
}

export interface SupportThreadDetail extends SupportThread {
  entries: SupportEntry[];
}

export const SUPPORT_SUBJECT_MAX = 200;
export const SUPPORT_BODY_MAX = 5000;

export async function listSupportThreads(signal?: AbortSignal): Promise<SupportThread[]> {
  const res = await apiGet<{ ok: true; data: { items: SupportThread[] } }>(
    '/api/portal/support/messages?limit=100',
    signal,
  );
  return res.data?.items ?? [];
}

export async function getSupportThread(
  id: number,
  signal?: AbortSignal,
): Promise<SupportThreadDetail> {
  const res = await apiGet<{ ok: true; thread: SupportThreadDetail }>(
    `/api/portal/support/messages/${id}`,
    signal,
  );
  return res.thread;
}

export async function createSupportThread(input: {
  subject: string;
  body: string;
  requestId: string;
}): Promise<SupportThread> {
  const res = await apiPost<{ ok: true; message: SupportThread }>('/api/portal/support/messages', {
    subject: input.subject,
    body: input.body,
    request_id: input.requestId,
  });
  return res.message;
}

export async function sendSupportReply(
  id: number,
  input: { body: string; requestId: string },
): Promise<SupportThreadDetail> {
  const res = await apiPost<{ ok: true; thread: SupportThreadDetail }>(
    `/api/portal/support/messages/${id}/replies`,
    { body: input.body, request_id: input.requestId },
  );
  return res.thread;
}

export type SupportTone = 'waiting' | 'replied' | 'closed';

/**
 * What the customer sees on a thread: closed, a reply from Provide Cars is
 * waiting to be read (staff wrote last), or their message is with the team.
 */
export function supportThreadStatus(t: Pick<SupportThread, 'status' | 'last_author'>): {
  tone: SupportTone;
  label: string;
} {
  if (t.status === 'closed') return { tone: 'closed', label: 'Closed' };
  if (t.last_author === 'support') return { tone: 'replied', label: 'Reply from Provide Cars' };
  return { tone: 'waiting', label: 'Sent — awaiting reply' };
}

/** "13 Oct 2026, 17:13 JST". */
export function formatSupportTime(value: string | null | undefined): string {
  const d = parsePortalTime(value);
  if (!d) return '—';
  const text = d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Tokyo',
  });
  return `${text} JST`;
}

/** The error code shown next to "could not be sent". */
export function supportErrorCode(err: unknown): string {
  if (err instanceof PortalApiError || err instanceof PortalFeatureDisabledError) return err.code;
  return 'SEND_FAILED';
}

/** Per-submission idempotency token (the BFF forwards it as Idempotency-Key). */
export function newRequestId(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c?.randomUUID) return c.randomUUID();
  const hex = (n: number) =>
    Math.floor(Math.random() * 16 ** n)
      .toString(16)
      .padStart(n, '0');
  return `${hex(8)}-${hex(4)}-${hex(4)}-${hex(4)}-${hex(12)}`;
}
